import {
  hitsByCountryPath,
  profileWorksJsonPath,
  researchInterestWorksPath,
  universityHitCountsPath,
} from './destinations';

// Every request this autotest makes over fetch goes through here, under one
// policy, so that no arm ever states a network policy of its own — arms/ calls
// no fetch of its own, and this file is the reason it never needs to.
// window.Academia's JSONP batchers are the other transport and live in
// batchFetchers, under the same unknown-is-not-zero rule.
//
// The policy, in full:
//
//  - same-origin credentials. Every endpoint here is scoped server-side to the
//    session, and the two stats endpoints answer 401 — not a thinner body — to
//    anyone the session does not put behind Analytics::Permissions.
//  - a hard deadline on EVERY request. Nothing here is worth a caller that
//    never settles: an arm awaiting one is a panel that never mounts, and one
//    dead request inside a Promise.all strands the whole arm.
//  - non-200 is UNKNOWN, never zero, and so is a body that will not parse.
//    Strict 200 rather than response.ok is not pedantry — a sibling action on
//    the same stats controller renders {"status":"processing"} with a 202 while
//    an aggregate builds, and response.ok reads that as data.
//  - the four endpoint readers — workFacts, readerCountries, universityReach,
//    fieldFeedWorks — never reject, whatever comes back over the wire, and
//    neither does getJson. Unknown is spelled per endpoint: null where the whole
//    answer is missing, an absent map entry where one record is. once() is the
//    deliberate exception: it rethrows what a CALLER's own loader did, because
//    swallowing that would hand an arm a silent undefined in place of its bug.
//
// Response parsing lives here beside the endpoint it belongs to, never in an
// arm. An arm that knows a payload key is an arm that has to be edited when the
// serializer moves.

// Sized for the slowest thing behind these URLs: the university figure sits
// behind a 30-day Rails.cache entry that rebuilds off a 28-day DynamoDB hit
// scan on a miss. Same figure as batchFetchers' deadline, arrived at
// separately — that one covers a debounce plus a JSONP round trip.
export const DEADLINE_MS = 4000;

const OK = 200;

// The endpoint's other callers ask for 20. Each entry is a Caches::WorkJson
// fetch with a heavy eager load on miss, so this is already generous, and it
// caps facts at the first 100 works: an arm ranking a corpus longer than that
// sees the tail as unknown rather than as zero.
const WORKS_PER_PAGE = 100;

// Over-fetch. The interest feed is a plain tag index, so the caller's own
// papers come back in it and get filtered out client-side; asking for exactly
// as many as a panel shows would thin the panel on a niche interest.
// Api::V0::ResearchInterestsController caps page_size at 100.
const FEED_PAGE_SIZE = 10;

// Facts an arm needs that the rendered card does not carry. page_count is the
// one that matters, it is the ranking key and exists nowhere in the DOM. Views
// are rendered as a spinner and filled asynchronously, so they are not readable
// off the card either. Abstract text IS on the card, use ProfileCard#abstractText.
export type WorkFacts = {
  pageCount: number | null;
  views: number | null;
  // Tri-state, and it has to stay one: the arms that list "papers with no file"
  // key on an explicit false. null is a work the endpoint did not describe, and
  // listing that one would be an accusation rather than a reminder.
  hasFile: boolean | null;
  title: string | null;
  url: string | null;
  // The abstract exactly as works_json carries it: null is a work the endpoint
  // did not describe, a blank string is a work with no abstract, and the two
  // are different facts.
  summary: string | null;
  // The FIRST downloadable attachment's download destination. Preferentially
  // its download_file_url — the stable /attachments/:id/download_file route
  // control's own card anchors use, which 302s to a freshly signed file URL
  // and records the download. NEVER prefer the sibling download_url: that is a
  // CloudFront URL signed for one hour at CACHE-WRITE time, and works_json is
  // cached per work-update with no TTL, so on any warm profile its signature
  // is long expired and the link 403s. It survives only as the fallback for a
  // payload that dropped the stable key. null spells "no attachment" and
  // "unknown" alike, so whether a file exists stays hasFile's question.
  downloadUrl: string | null;
  // publication_year_with_fallback: the work's own publication_date year, or
  // the matched bib entry's when the metadata carries none. null means neither
  // source knows — never "old".
  year: number | null;
  // The profile section the work files under — "Papers", "Book Chapters" — the
  // same names the section chips carry. A sibling of work_json in the entry,
  // not a work_json key. null is an entry the endpoint left unfiled.
  section: string | null;
};

// Caches::GenerateWorkJson#get_hash is a hand-rolled hash, not a serializer.
// These are its key names, not the ones WorkSerializer uses. all_time_views is
// there only because get_hash defaults include_work_stats to true.
// downloadable_attachments, never the tempting has_pdf beside it: has_pdf means
// "any attachment is a PDF", so a docx-only work is downloadable with has_pdf
// false, and keying "no file" on it accuses every non-PDF corpus.
type WorkJsonEntry = {
  section_name?: unknown;
  work_json?: {
    id?: number;
    page_count?: number | null;
    all_time_views?: number | null;
    downloadable_attachments?: unknown;
    title?: string | null;
    url?: string | null;
    summary?: string | null;
    publication_year_with_fallback?: number | null;
  };
};

export type Country = { name: string; hits: number };

// count is every country the aggregate saw; top is only the ones it could name.
// The two differ, so count is never derivable from top.length — see readCountries.
export type ReaderCountries = { count: number; top: Country[] };

export type UniversityReach = { count: number; name: string };

export type FeedPaper = {
  id: number;
  title: string;
  url: string;
  // owner_id folded in with ordered_authors: the work's owner is not guaranteed
  // to appear among its authors, and the only question a caller has is whether
  // the profile's own user wrote this paper.
  authorIds: number[];
  // display_name per ordered_authors entry, in that order, blank ones dropped.
  // Never zip this with authorIds: the folded-in owner has an id and no name,
  // so the two lists share no indexing.
  authorNames: string[];
};

type FeedWorkEntry = {
  id?: unknown;
  title?: unknown;
  url?: unknown;
  owner_id?: unknown;
  ordered_authors?: unknown;
};

async function readJson<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (response.status !== OK) return null;

    return (await response.json()) as T;
  } catch {
    return null;
  }
}

// No AbortController: the deadline exists so the CALLER settles, and a GET the
// browser finishes into a value nobody reads costs nothing. Resolving twice is
// a no-op, so a late response is discarded rather than raced back into an arm
// that has already rendered without it. readJson cannot reject, which is what
// keeps the losing side of this race from surfacing as an unhandled rejection.
export function getJson<T>(
  url: string,
  options: { deadlineMs?: number } = {},
): Promise<T | null> {
  const deadlineMs = options.deadlineMs ?? DEADLINE_MS;

  return new Promise((resolve) => {
    const deadline = window.setTimeout(() => resolve(null), deadlineMs);

    void readJson<T>(url).then((value) => {
      window.clearTimeout(deadline);
      resolve(value);
    });
  });
}

const memo = new Map<string, Promise<unknown>>();

// Memoized per pageview and fetched only when something asks, so an arm that
// works off the DOM alone costs no request, and two arms — or one arm and its
// own second pass — share one.
export function once<T>(key: string, load: () => Promise<T>): Promise<T> {
  const cached = memo.get(key) as Promise<T> | undefined;
  if (cached != null) return cached;

  // A rejection is evicted rather than remembered. Nothing below can reject, so
  // a rejection here is a caller's own loader failing, and caching it would
  // make one bad moment permanent for the rest of the pageview. The identity
  // check keeps an eviction from removing a newer entry stored after a reset.
  // The executor form is what turns a load() that throws synchronously — before
  // it ever returns a promise — into a rejection instead of a throw.
  const pending: Promise<T> = new Promise<T>((resolve) => {
    resolve(load());
  }).catch((error: unknown) => {
    if (memo.get(key) === pending) memo.delete(key);

    throw error;
  });

  memo.set(key, pending);

  return pending;
}

export function resetPageData(): void {
  memo.clear();
}

function readWorkFacts(payload: unknown): Map<number, WorkFacts> {
  const wrapped = (payload as { works_json?: unknown } | null)?.works_json;
  const entries = Array.isArray(payload) ? payload : wrapped;
  if (!Array.isArray(entries)) return new Map();

  return new Map(
    (entries as WorkJsonEntry[]).flatMap((entry) => {
      const work = entry?.work_json;
      if (work?.id == null) return [];

      const attachments = work.downloadable_attachments;
      const first = Array.isArray(attachments)
        ? (attachments[0] as
            | { download_url?: unknown; download_file_url?: unknown }
            | undefined)
        : undefined;
      // ?s=profile is the click_source control's card anchor sends, so an
      // arm's download counts exactly as a card download does. The stable
      // route never carries a query of its own; the signed fallback already
      // carries its signature's.
      const stable = first?.download_file_url;
      const signed = first?.download_url;
      const downloadUrl =
        typeof stable === 'string' && stable !== ''
          ? `${stable}?s=profile`
          : signed;
      const sectionName = entry.section_name;
      const section =
        typeof sectionName === 'string' && sectionName.trim() !== ''
          ? sectionName.trim()
          : null;

      return [
        [
          work.id,
          {
            pageCount: work.page_count ?? null,
            views: work.all_time_views ?? null,
            hasFile: Array.isArray(attachments) ? attachments.length > 0 : null,
            title: work.title ?? null,
            url: work.url ?? null,
            summary: work.summary ?? null,
            downloadUrl:
              typeof downloadUrl === 'string' && downloadUrl !== ''
                ? downloadUrl
                : null,
            year: work.publication_year_with_fallback ?? null,
            section,
          },
        ] as [number, WorkFacts],
      ];
    }),
  );
}

// A missing entry means unknown, never a work with nothing to say: a failed
// request and a corpus past WORKS_PER_PAGE are both spelled "absent", and so is
// the whole map when the request dies.
export function workFacts(
  viewedUserId: number,
): Promise<Map<number, WorkFacts>> {
  return once(`workFacts:${viewedUserId}`, async () =>
    readWorkFacts(
      await getJson(profileWorksJsonPath(viewedUserId, 0, WORKS_PER_PAGE)),
    ),
  );
}

// The same facts under the same absent-means-unknown rule, off pages 0-2 in
// parallel. The 300-work cap is deliberate: for a larger corpus the picks come
// from the first 300 works in the profile's own section order, and the tail
// stays unknown rather than zero. A page the deadline takes merges as empty,
// so one dead request thins the map without killing it.
export function deepWorkFacts(
  viewedUserId: number,
): Promise<Map<number, WorkFacts>> {
  return once(`deepWorkFacts:${viewedUserId}`, async () => {
    const pages = await Promise.all(
      [0, 1, 2].map((page) =>
        getJson(profileWorksJsonPath(viewedUserId, page, WORKS_PER_PAGE)),
      ),
    );

    return new Map(pages.flatMap((page) => [...readWorkFacts(page)]));
  });
}

function readCountries(payload: unknown): ReaderCountries | null {
  const totals = (payload as { countries?: { totals?: unknown } } | null)
    ?.countries?.totals;
  if (!Array.isArray(totals)) return null;

  const rows = totals as Array<{
    country_name?: unknown;
    all_time_hit_count?: unknown;
  }>;

  const top = rows
    .flatMap((row) => {
      const name =
        typeof row?.country_name === 'string' ? row.country_name.trim() : '';
      // country_name is null for a code we hold no name for, and those rows
      // still happened, so they are dropped from top but kept in count. The
      // count column is NOT NULL, so a non-finite one is a malformed payload
      // rather than a country with no hits; sorting it last is the honest
      // degradation, dropping the country's name is not.
      const hits = Number(row?.all_time_hit_count);

      return name === ''
        ? []
        : [{ name, hits: Number.isFinite(hits) ? hits : 0 }];
    })
    .sort((a, b) => b.hits - a.hits);

  return { count: totals.length, top };
}

// A count of 0 is what the endpoint said, not a failure — but it is not proof
// of a reader-less author either: Analytics::AggregateHitCounts short-circuits
// to [] when attention_db reads are disabled, and the controller caches that
// for six hours. Gate a panel on count > 0 and phrase nothing as a zero.
//
// 401 rather than 200 whenever the viewer may not see these analytics:
// Analytics::Permissions#can_view_analytics? is public-analytics OR admin OR
// self. For an owner-mode arm that is always true; a visitor-mode arm gets null
// for almost every profile it runs on.
export function readerCountries(
  userId: number,
): Promise<ReaderCountries | null> {
  return once(`readerCountries:${userId}`, async () =>
    readCountries(await getJson(hitsByCountryPath(userId))),
  );
}

type UniversityRecord = { name?: unknown; display_name?: unknown };

// Two shapes, not one. With a university found the controller renders the raw
// University record, and include_root_in_json wraps AR records once more — the
// columns sit under university.university, and display_name is nullable. Under
// Premium::UniversityHitsPremiumTrigger's two-university floor it renders a
// hand-built fallback whose inner university is null instead — same 200, no
// error. The unwrapped depth is accepted too, so a server-side root: false
// cannot silence the reader.
function readUniversities(payload: unknown): UniversityReach | null {
  const data = payload as {
    university?:
      | (UniversityRecord & { university?: UniversityRecord | null })
      | null;
    uni_count?: unknown;
  } | null;

  const record = data?.university?.university ?? data?.university;
  const named = [record?.display_name, record?.name].find(
    (value): value is string =>
      typeof value === 'string' && value.trim() !== '',
  );
  const count = Number(data?.uni_count);
  if (named == null || !Number.isFinite(count) || count < 1) return null;

  return { count, name: named.trim() };
}

// Rolling 28 days, from a different pipeline to the all-time country totals, so
// never phrase the two as one period. null covers both unknown and "fewer than
// two universities we could identify" — there is no nameable answer either way.
export function universityReach(
  userId: number,
): Promise<UniversityReach | null> {
  return once(`universityReach:${userId}`, async () =>
    readUniversities(await getJson(universityHitCountsPath(userId))),
  );
}

function idOf(value: unknown): number | null {
  const id = Number(value);

  return Number.isFinite(id) && id > 0 ? id : null;
}

function stringOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function authorIdsOf(work: FeedWorkEntry): number[] {
  const authors = Array.isArray(work.ordered_authors)
    ? work.ordered_authors
    : [];
  const ids = authors.flatMap((author: { id?: unknown } | null) => {
    const id = idOf(author?.id);

    return id == null ? [] : [id];
  });

  const owner = idOf(work.owner_id);

  return owner == null || ids.includes(owner) ? ids : [owner, ...ids];
}

function authorNamesOf(work: FeedWorkEntry): string[] {
  const authors = Array.isArray(work.ordered_authors)
    ? work.ordered_authors
    : [];

  return authors.flatMap((author: { display_name?: unknown } | null) => {
    const name = stringOf(author?.display_name);

    return name === '' ? [] : [name];
  });
}

function readFeed(payload: unknown): FeedPaper[] {
  const works = (payload as { works?: unknown } | null)?.works;
  if (!Array.isArray(works)) return [];

  return (works as FeedWorkEntry[]).flatMap((work) => {
    const id = idOf(work?.id);
    const title = stringOf(work?.title);
    const url = stringOf(work?.url);
    if (id == null || title === '' || url === '') return [];

    return [
      {
        id,
        title,
        url,
        authorIds: authorIdsOf(work),
        authorNames: authorNamesOf(work),
      },
    ];
  });
}

// A tag index, not a recommender: Tagging.for_research_interest is a plain
// indexable-works join ordered by work id, so a thin-graph author gets exactly
// the feed anyone else gets for that interest. The one endpoint here whose
// unknown and whose empty are the same value — the caller renders nothing
// either way, and there is no phrasing of an empty feed that would be safe.
export function fieldFeedWorks(interestId: number): Promise<FeedPaper[]> {
  return once(`fieldFeedWorks:${interestId}`, async () =>
    readFeed(
      await getJson(researchInterestWorksPath(interestId, FEED_PAGE_SIZE)),
    ),
  );
}
